import { Box, Typography } from '@mui/material';
import { formatCurrency } from '../../../utils/currency';
import { hideBelowSx } from '../collapse';
import { Money } from '../primitives';
import { LEAD_WIDTH, VALUE_WIDTH, type LedgerCollapse } from '../types';

export interface ItemSubtotalStripProps {
  collapse: LedgerCollapse;
  /**
   * Whole percent of the item's target that is booked. Null means the figure
   * is withheld from this viewer, not that it is zero — the same rule `Money`
   * follows, and a viewer without financial permission has none of the three.
   */
  percentBooked: number | null;
  /** What is left of the target. Null when withheld. */
  remaining: number | null;
  /** Sum of the peer rows above. Null when withheld. */
  bookedTotal: number | null;
  showValues?: boolean;
}

/** Same stand-in `Money` uses, so a redacted strip reads like a redacted row. */
const REDACTED = '··';

/**
 * Caps a run of peer booking rows for one part-booked item: "63% booked ·
 * £650.00 remaining", with the booked total on the Value column. Text only —
 * an inline progress bar made row heights ragged.
 */
export const ItemSubtotalStrip = ({
  collapse,
  percentBooked,
  remaining,
  bookedTotal,
  showValues = true,
}: ItemSubtotalStripProps) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 0.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ width: LEAD_WIDTH, flexShrink: 0 }} />
    <Typography
      variant="caption"
      color="text.secondary"
      noWrap
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {percentBooked == null ? REDACTED : `${percentBooked}%`} booked
      {showValues && (
        <Box component="span" sx={hideBelowSx(collapse)}>
          {' '}
          · {remaining == null ? REDACTED : formatCurrency(remaining)} remaining
        </Box>
      )}
    </Typography>
    <Box sx={{ flex: 1 }} />
    {showValues && (
      <Box sx={{ width: VALUE_WIDTH, flexShrink: 0, textAlign: 'right' }}>
        <Money value={bookedTotal} />
      </Box>
    )}
  </Box>
);
